import type { components } from '../../../generated/schema';
import { api } from '../api';
import type {
  DocumentIngestionStatus,
  GraphStatsResponse,
} from '../../types/knowledge';

type Schemas = components['schemas'];

export type KnowledgeSourcesResponse = Schemas['KnowledgeSourcesResponse'];
export type KnowledgeSource = Schemas['KnowledgeSourceSchema'];
export type ConnectorResponse = Schemas['ConnectorStateSchema'];
export type SearchResponse = Schemas['SearchResponseSchema'];
export type AskResponse = Schemas['AskResponseSchema'];
export interface UploadResponse {
  ids: string[];
  uploads?: Array<{
    document_id: string;
    filename: string;
    status: string;
  }>;
  artifacts?: Array<{
    id: string;
    title: string;
  }>;
}

/**
 * Backend `DocumentContentResponse` (see `app/backend/schemas/workspace.py`):
 *
 * class DocumentContentResponse(BaseModel):
 *     content: str
 *     source: str
 *
 * The current OpenAPI typings do not expose this schema yet, so we define the
 * matching shape locally to keep the frontend aligned with the backend without
 * blocking on a types regeneration step.
 */
export interface DocumentContentResponse {
  content: string;
  source: string;
}

// Export a convenience SearchHit type for consumers
export type SearchHit = SearchResponse['hits'][number];

export type ConnectorRequest = Record<string, unknown>;

export interface AskRequestPayload {
  question: string;
}

export const listSources = async (): Promise<KnowledgeSource[]> => {
  // const response = await api.get('api/sources').json<KnowledgeSourcesResponse>();
  // request with the refresh arg
  const response = await api
    .get('api/sources', { searchParams: { refresh: 'true' } })
    .json<KnowledgeSourcesResponse>();
  return response.sources;
};

export const uploadFiles = async (files: File[]): Promise<UploadResponse> => {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  return api.post('api/upload', { body: form }).json<UploadResponse>();
};

//! Not yet implemented in the backend
export const connectSource = async (
  provider: string,
  payload: ConnectorRequest,
): Promise<ConnectorResponse> => {
  return api.post(`api/connect/${provider}`, { json: payload }).json<ConnectorResponse>();
};

export const search = async (q: string): Promise<SearchResponse> => {
  return api.get('api/search', { searchParams: { q } }).json<SearchResponse>();
};

export const ask = async (body: AskRequestPayload): Promise<AskResponse> => {
  return api.post('api/ask', { json: body }).json<AskResponse>();
};

export const getDocumentContent = async (source: string): Promise<DocumentContentResponse> => {
  return api
    .get('api/document/content', { searchParams: { source } })
    .json<DocumentContentResponse>();
};

export interface UploadWorkspaceMediaResponse {
  url: string;
  filename: string;
  path: string;
}

/** Upload image to media/user/{artifact_id}/images (same path as OCR). Pass artifactId for document images. */
export const uploadWorkspaceMedia = async (
  file: File,
  artifactIdOrDocStem: string,
): Promise<UploadWorkspaceMediaResponse> => {
  const form = new FormData();
  form.append('file', file);
  form.append('artifact_id', artifactIdOrDocStem);
  return api.post('api/knowledge/media/upload', { body: form }).json<UploadWorkspaceMediaResponse>();
};

export interface DocumentReference {
  id: string;
  title: string;
  type: string;
}

export interface SensitiveDataEntry {
  code: string;
  data_type: string;
  original_value: string;
  usage_count: number;
  documents: DocumentReference[];
  other_occurrences: DocumentReference[];
}

export interface SensitiveDataListResponse {
  entries: SensitiveDataEntry[];
}

export const listSensitiveData = async (): Promise<SensitiveDataEntry[]> => {
  const response = await api.get('api/knowledge/sensitive-data').json<SensitiveDataListResponse>();
  return response.entries;
};

export interface DeleteSensitiveDataResponse {
  success: boolean;
  code: string;
  original_value?: string;
  updated_documents?: string[];
}

export const deleteSensitiveData = async (code: string): Promise<DeleteSensitiveDataResponse> => {
  return api.delete(`api/knowledge/sensitive-data/${encodeURIComponent(code)}`).json();
};

export interface UpdateSensitiveDataRequest {
  original_value?: string;
  data_type?: string;
}

export interface UpdateSensitiveDataResponse {
  success: boolean;
  entry: {
    code: string;
    data_type: string;
    original_value: string;
    usage_count: number;
  };
  affected_documents?: string[];
}

export const updateSensitiveData = async (
  code: string,
  updates: UpdateSensitiveDataRequest,
): Promise<UpdateSensitiveDataResponse> => {
  return api
    .patch(`api/knowledge/sensitive-data/${encodeURIComponent(code)}`, { json: updates })
    .json();
};

export type { DocumentIngestionStatus, GraphStatsResponse } from '../../types/knowledge';

export const getGraphStats = async (workspaceId?: string): Promise<GraphStatsResponse> => {
  return api
    .get('api/graph/stats', {
      searchParams: workspaceId ? { workspace_id: workspaceId } : undefined,
    })
    .json<GraphStatsResponse>();
};

export const getDocumentStatus = async (
  documentId: string,
): Promise<DocumentIngestionStatus> => {
  return api
    .get(`api/documents/${encodeURIComponent(documentId)}/status`)
    .json<DocumentIngestionStatus>();
};

export interface ProcessChunksResponse {
  document_id: string;
  total_chunks: number;
  total_embedded: number;
  status: string;
}

export const processDocumentChunks = async (
  documentId: string,
  workspaceId?: string,
): Promise<ProcessChunksResponse> => {
  return api
    .post(`api/documents/${encodeURIComponent(documentId)}/process-chunks`, {
      json: { workspace_id: workspaceId || 'default' },
    })
    .json<ProcessChunksResponse>();
};

export interface EntityExtractionResponse {
  extraction: {
    document_id: string;
    entities: Array<{
      name: string;
      type: string;
      confidence: number;
    }>;
    relationships: Array<{
      source: string;
      target: string;
      type: string;
      confidence: number;
    }>;
    confidence: number;
  };
  graph_summary: Record<string, unknown>;
}

export const extractDocumentEntities = async (
  documentId: string,
  workspaceId?: string,
): Promise<EntityExtractionResponse> => {
  return api
    .post(`api/documents/${encodeURIComponent(documentId)}/extract-entities`, {
      json: { workspace_id: workspaceId || 'default' },
    })
    .json<EntityExtractionResponse>();
};

export interface SearchChunksRequest {
  query: string;
  limit?: number;
  document_id?: string;
  workspace_id?: string;
}

export interface ChunkSearchResult {
  score: number;
  text: string;
  document_id: string;
  citation_ref: string;
  chunk_index: number;
  metadata: Record<string, unknown>;
}

export const searchChunks = async (
  request: SearchChunksRequest,
): Promise<ChunkSearchResult[]> => {
  return api
    .post('api/documents/search', { json: request })
    .json<ChunkSearchResult[]>();
};

export interface GraphEntity {
  name: string;
  types: string[];
  confidence?: number | null;
  properties?: Record<string, unknown>;
}

export interface GraphRelationship {
  source: string;
  type: string;
  target: string;
  confidence?: number | null;
  properties?: Record<string, unknown>;
}

export interface GraphEntitiesResponse {
  entities: GraphEntity[];
  count: number;
}

export interface GraphRelationshipsResponse {
  relationships: GraphRelationship[];
  count: number;
}

export const getGraphEntities = async (
  workspaceId?: string,
  limit = 300,
): Promise<GraphEntitiesResponse> => {
  return api
    .get('api/graph/entities', {
      searchParams: {
        limit: String(limit),
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
      },
    })
    .json<GraphEntitiesResponse>();
};

export const getGraphRelationships = async (
  workspaceId?: string,
  limit = 400,
): Promise<GraphRelationshipsResponse> => {
  return api
    .get('api/graph/relationships', {
      searchParams: {
        limit: String(limit),
        ...(workspaceId ? { workspace_id: workspaceId } : {}),
      },
    })
    .json<GraphRelationshipsResponse>();
};
